import { useEffect } from 'react';

const selector = 'button:not(:disabled), a[href]:not(.skip-link):not([tabindex="-1"]), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])';

function candidates(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>(selector)].filter((element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && getComputedStyle(element).visibility !== 'hidden';
  });
}

export function normalizeRemoteKey(event: Pick<KeyboardEvent, 'key' | 'keyCode'>): string {
  const mapped = new Map<number, string>([
    [37, 'ArrowLeft'], [38, 'ArrowUp'], [39, 'ArrowRight'], [40, 'ArrowDown'],
    [4, 'Escape'], [461, 'Escape'], [10009, 'Escape'],
  ]);
  return mapped.get(event.keyCode) ?? event.key;
}

/**
 * True for anything that consumes raw keystrokes: a text-like input, a
 * textarea, or a contenteditable region. Checkboxes, radios and buttons are
 * deliberately excluded — those still want D-pad navigation.
 */
function isTextEntry(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  if (element instanceof HTMLTextAreaElement) return true;
  if (element instanceof HTMLInputElement) {
    const nonText = ['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'file', 'color', 'image'];
    return !nonText.includes(element.type);
  }
  return false;
}

function restoreFocus(preferredKey: string | null): void {
  const available = candidates();
  const preferred = preferredKey === null ? undefined : available.find((element) => element.dataset.focusKey === preferredKey);
  (preferred ?? document.querySelector<HTMLElement>('[data-focus-default]') ?? available[0])?.focus({ preventScroll: true });
}

export function useTvNavigation(): void {
  useEffect(() => {
    try {
      window.tizen?.tvinputdevice.registerKeyBatch(['MediaPlayPause', 'ColorF0Red', 'ColorF1Green', 'ColorF2Yellow', 'ColorF3Blue']);
    } catch {
      // Registration is best-effort; mandatory D-pad and Return keys do not require registration.
    }
    let lastKey: string | null = null;
    const onFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.dataset.focusKey !== undefined) lastKey = target.dataset.focusKey;
    };
    const onKey = (event: KeyboardEvent) => {
      const key = normalizeRemoteKey(event);
      // While someone is typing, the keyboard belongs to them and to nothing
      // else. Backspace deletes a character; the arrows move the caret. This
      // guard is the whole reason a player can edit a wish at all: without it
      // the D-pad handler below swallowed Backspace on every screen, and the
      // text could be typed but never corrected.
      if (isTextEntry(document.activeElement)) {
        if (key === 'Escape') (document.activeElement as HTMLElement).blur();
        return;
      }
      if (key === 'Escape' || key === 'Backspace') {
        const close = document.querySelector<HTMLElement>('[role="dialog"] button[data-focus-default]');
        if (close !== null) close.click();
        else if (!location.pathname.startsWith('/room/') && !location.pathname.startsWith('/screen/')) history.back();
        event.preventDefault();
        return;
      }
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;
      const available = candidates();
      if (available.length === 0) return;
      const active = document.activeElement instanceof HTMLElement && available.includes(document.activeElement)
        ? document.activeElement
        : null;
      if (active === null) {
        restoreFocus(lastKey);
        event.preventDefault();
        return;
      }
      const origin = active.getBoundingClientRect();
      const ox = origin.left + origin.width / 2;
      const oy = origin.top + origin.height / 2;
      const direction = key;
      const next = available
        .filter((element) => element !== active)
        .map((element) => {
          const rect = element.getBoundingClientRect();
          const dx = rect.left + rect.width / 2 - ox;
          const dy = rect.top + rect.height / 2 - oy;
          const allowed = direction === 'ArrowRight' ? dx > 4 : direction === 'ArrowLeft' ? dx < -4 : direction === 'ArrowDown' ? dy > 4 : dy < -4;
          const primary = direction === 'ArrowRight' || direction === 'ArrowLeft' ? Math.abs(dx) : Math.abs(dy);
          const secondary = direction === 'ArrowRight' || direction === 'ArrowLeft' ? Math.abs(dy) : Math.abs(dx);
          return { element, allowed, score: primary + secondary * 2.5 };
        })
        .filter((entry) => entry.allowed)
        .sort((left, right) => left.score - right.score)[0]?.element;
      next?.focus({ preventScroll: false });
      event.preventDefault();
    };
    const observer = new MutationObserver(() => {
      if (!(document.activeElement instanceof HTMLElement) || document.activeElement === document.body || !document.activeElement.isConnected) {
        restoreFocus(lastKey);
      }
    });
    document.addEventListener('focusin', onFocus);
    document.addEventListener('keydown', onKey);
    observer.observe(document.getElementById('main') ?? document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
}
